/* eslint-disable react-hooks/exhaustive-deps */
import {
  Box,
  Button,
  Checkbox,
  Menu,
  MenuButton,
  MenuDivider,
  MenuList,
  Radio,
  RadioGroup,
  Stack,
  useColorMode,
  useColorStyle,
} from '@tonic-one/react';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useDashboardDaysStore } from '../../../models/useDashboardDaysModel';
import { reportPendo } from '../../../utils/setupPendo';

const TIME_RANGES = [
  { day: '24h', text: 'dashboard.chart.column.last24h' },
  { day: '1w', text: 'dashboard.chart.column.last1w' },
  { day: '1M', text: 'dashboard.chart.column.last1M' },
];

export enum TimeRangeEnum {
  Last24h = '24h',
  Last1w = '1w',
  Last1M = '1M',
}

export default function TimeRangeSelector(props: { widgetId: string }) {
  const [colorMode] = useColorMode();
  const [colorStyle] = useColorStyle({ colorMode });
  const { t } = useTranslation();

  const divRef = useRef<HTMLDivElement>(null);

  const daysModel = useDashboardDaysStore((model) => [model.days]);
  const saveDay = daysModel.days[props.widgetId] || '24h';
  const defaultDay = (
    TIME_RANGES.find((time) => time.day === saveDay) || TIME_RANGES[0]
  ).day;
  const [day, setDay] = useState(defaultDay);
  const [all, setAll] = useState(false);

  useEffect(() => {
    if (!daysModel.days[props.widgetId]) {
      daysModel.setDay(props.widgetId, '24h');
    }
  }, []);
  useEffect(() => {
    setDay(saveDay);
  }, [saveDay]);

  const onApply = () => {
    if (!all) {
      daysModel.setDay(props.widgetId, day);
    } else {
      daysModel.setAllDays(day);
    }
    reportPendo('TimeRangeSelector.Apply.Click', {
      widgetName: !all ? props.widgetId : 'all',
      timeRange: day,
    });
    setAll(false);
  };

  const time =
    TIME_RANGES.find((time) => time.day === saveDay) || TIME_RANGES[0];
  return (
    <Menu placement="bottom-end">
      {({ onClose }: any) => {
        return (
          <>
            <MenuButton
              minWidth="108px"
              font="2x"
              variant="ghost"
              px={4}
              color={colorStyle.color.secondary}
            >
              {t(time.text)}
            </MenuButton>
            <MenuList minWidth={225}>
              <Box ref={divRef} font="2x" px="4x">
                <Box pt="1x">
                  <RadioGroup
                    value={day}
                    onChange={(newDay: string) => setDay(newDay)}
                  >
                    <Stack spacing="2x" shouldWrapChildren>
                      {TIME_RANGES.map(({ day, text }) => (
                        <Radio
                          data-track={`TimeRangeSelector.${text}`}
                          key={day}
                          value={day}
                          color={colorStyle.color.primary}
                          lineHeight="20px"
                        >
                          {t(text)}
                        </Radio>
                      ))}
                    </Stack>
                  </RadioGroup>
                </Box>

                <MenuDivider />

                <Box pb="1x">
                  <Checkbox
                    data-track={`TimeRangeSelector.applyToAll`}
                    checked={all}
                    onChange={(e: any) => setAll(e.target.checked)}
                    color={colorStyle.color.primary}
                    lineHeight="20px"
                  >
                    {t('dashboard.chart.timeRangeSelector.applyToAll')}
                  </Checkbox>
                  <Stack direction="row" spacing="2x" mt="4x">
                    <Button
                      data-track={`TimeRangeSelector.apply`}
                      variant="primary"
                      onClick={() => {
                        onApply();
                        onClose();
                      }}
                    >
                      {t('common.button.apply')}
                    </Button>
                    <Button
                      variant="default"
                      onClick={() => {
                        setDay(saveDay);
                        setAll(false);
                        onClose();
                      }}
                    >
                      {t('common.button.cancel')}
                    </Button>
                  </Stack>
                </Box>
              </Box>
            </MenuList>
          </>
        );
      }}
    </Menu>
  );
}
